import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  ChannelProviderConfigDetail,
  ChannelProviderConfigSummary,
  ChannelProviderDescriptor,
  DeliveryDashboard,
  DeliveryMetricBucket,
  GmailOAuthStartResult,
  KavenegarCallbackTokenReveal,
  MessageTemplate,
  MessageTemplateRenderResult,
  MessageTemplateVersion,
  NotificationDispatchPolicy,
  NotificationMessageDetailExtended,
  NotificationMessagesQuery,
  PagedNotificationMessages,
  PagedSuppressions,
  ProviderHealthDashboard,
  ProviderOperationResult,
  QuotaPolicy,
  QuotaUsage,
  SenderDomainReadiness,
  SuppressionEntry
} from './notification.models';
import { notificationCategoryToApiValue, notificationChannelToApiValue } from './notification.utils';

@Injectable({ providedIn: 'root' })
export class NotificationService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.apiBaseUrl}/api/v1/notifications`;

  listMessages(query: NotificationMessagesQuery = {}): Observable<PagedNotificationMessages> {
    let params = new HttpParams();
    if (query.messageKey) params = params.set('messageKey', query.messageKey);
    if (query.channel != null) {
      const channel = notificationChannelToApiValue(String(query.channel));
      if (channel) params = params.set('channel', channel);
    }
    if (query.status) params = params.set('status', query.status);
    if (query.providerKey) params = params.set('providerKey', query.providerKey);
    if (query.category != null) {
      const category = notificationCategoryToApiValue(String(query.category));
      if (category) params = params.set('category', category);
    }
    if (query.sourceModuleKey) params = params.set('sourceModuleKey', query.sourceModuleKey);
    if (query.correlationId) params = params.set('correlationId', query.correlationId);
    if (query.fromUtc) params = params.set('fromUtc', query.fromUtc);
    if (query.toUtc) params = params.set('toUtc', query.toUtc);
    if (query.skip != null) params = params.set('skip', String(query.skip));
    if (query.take != null) params = params.set('take', String(query.take));
    return this.http.get<PagedNotificationMessages>(`${this.base}/messages`, { params });
  }

  getMessage(messageId: string): Observable<NotificationMessageDetailExtended> {
    return this.http.get<NotificationMessageDetailExtended>(`${this.base}/messages/${messageId}`);
  }

  cancelMessage(messageId: string): Observable<{ status: string }> {
    return this.http.post<{ status: string }>(`${this.base}/messages/${messageId}/cancel`, {});
  }

  resendMessage(messageId: string): Observable<{ queued: number }> {
    return this.http.post<{ queued: number }>(`${this.base}/messages/${messageId}/resend`, {});
  }

  listProviderCatalog(): Observable<ChannelProviderDescriptor[]> {
    return this.http.get<ChannelProviderDescriptor[]>(`${this.base}/providers/catalog`);
  }

  listProviderConfigs(channel?: string | null): Observable<ChannelProviderConfigSummary[]> {
    let params = new HttpParams();
    if (channel) params = params.set('channel', channel);
    return this.http.get<ChannelProviderConfigSummary[]>(`${this.base}/providers/configs`, { params });
  }

  getProviderConfig(configId: string): Observable<ChannelProviderConfigDetail> {
    return this.http.get<ChannelProviderConfigDetail>(`${this.base}/providers/configs/${configId}`);
  }

  createSmtpProvider(payload: unknown): Observable<ChannelProviderConfigDetail> {
    return this.http.post<ChannelProviderConfigDetail>(`${this.base}/providers/smtp`, payload);
  }

  updateSmtpProvider(configId: string, payload: unknown): Observable<ChannelProviderConfigDetail> {
    return this.http.put<ChannelProviderConfigDetail>(`${this.base}/providers/smtp/${configId}`, payload);
  }

  createKavenegarProvider(payload: unknown): Observable<ChannelProviderConfigDetail> {
    return this.http.post<ChannelProviderConfigDetail>(`${this.base}/providers/kavenegar`, payload);
  }

  updateKavenegarProvider(configId: string, payload: unknown): Observable<ChannelProviderConfigDetail> {
    return this.http.put<ChannelProviderConfigDetail>(`${this.base}/providers/kavenegar/${configId}`, payload);
  }

  regenerateKavenegarCallbackToken(configId: string): Observable<KavenegarCallbackTokenReveal> {
    return this.http.post<KavenegarCallbackTokenReveal>(
      `${this.base}/providers/kavenegar/${configId}/regenerate-callback-token`,
      {});
  }

  enableProvider(configId: string): Observable<unknown> {
    return this.http.post(`${this.base}/providers/configs/${configId}/enable`, {});
  }

  disableProvider(configId: string): Observable<unknown> {
    return this.http.post(`${this.base}/providers/configs/${configId}/disable`, {});
  }

  deleteProvider(configId: string): Observable<unknown> {
    return this.http.delete(`${this.base}/providers/configs/${configId}`);
  }

  testSmtpConnection(configId: string): Observable<ProviderOperationResult> {
    return this.http.post<ProviderOperationResult>(`${this.base}/providers/smtp/${configId}/test-connection`, {});
  }

  testSmtpSend(configId: string, recipientAddress: string): Observable<ProviderOperationResult> {
    return this.http.post<ProviderOperationResult>(`${this.base}/providers/smtp/${configId}/test-send`, { recipientAddress });
  }

  listGmailAccounts(): Observable<ChannelProviderConfigSummary[]> {
    return this.http.get<ChannelProviderConfigSummary[]>(`${this.base}/providers/gmail`);
  }

  connectGmail(returnUrl?: string | null): Observable<GmailOAuthStartResult> {
    return this.http.post<GmailOAuthStartResult>(`${this.base}/providers/gmail/connect`, { returnUrl: returnUrl ?? null });
  }

  disconnectGmail(configId: string): Observable<unknown> {
    return this.http.post(`${this.base}/providers/gmail/${configId}/disconnect`, {});
  }

  testGmailSend(configId: string, recipientAddress: string): Observable<ProviderOperationResult> {
    return this.http.post<ProviderOperationResult>(`${this.base}/providers/gmail/${configId}/test-send`, { recipientAddress });
  }

  testKavenegarConnection(configId: string): Observable<ProviderOperationResult> {
    return this.http.post<ProviderOperationResult>(`${this.base}/providers/kavenegar/${configId}/test-connection`, {});
  }

  testKavenegarSend(configId: string, recipientAddress: string, messageBody?: string | null): Observable<ProviderOperationResult> {
    return this.http.post<ProviderOperationResult>(`${this.base}/providers/kavenegar/${configId}/test-send`, {
      recipientAddress,
      messageBody: messageBody ?? null
    });
  }

  listTemplates(channel?: string | null): Observable<MessageTemplate[]> {
    let params = new HttpParams();
    if (channel) params = params.set('channel', channel);
    return this.http.get<MessageTemplate[]>(`${this.base}/templates/`, { params });
  }

  createTemplate(payload: unknown): Observable<MessageTemplate> {
    return this.http.post<MessageTemplate>(`${this.base}/templates/`, payload);
  }

  listTemplateVersions(templateId: string): Observable<MessageTemplateVersion[]> {
    return this.http.get<MessageTemplateVersion[]>(`${this.base}/templates/${templateId}/versions`);
  }

  createTemplateVersion(templateId: string, payload: unknown): Observable<MessageTemplateVersion> {
    return this.http.post<MessageTemplateVersion>(`${this.base}/templates/${templateId}/versions`, payload);
  }

  updateTemplateVersion(templateId: string, versionId: string, payload: unknown): Observable<MessageTemplateVersion> {
    return this.http.put<MessageTemplateVersion>(`${this.base}/templates/${templateId}/versions/${versionId}`, payload);
  }

  publishTemplateVersion(templateId: string, versionId: string): Observable<MessageTemplateVersion> {
    return this.http.post<MessageTemplateVersion>(`${this.base}/templates/${templateId}/versions/${versionId}/publish`, {});
  }

  previewTemplate(payload: unknown): Observable<MessageTemplateRenderResult> {
    return this.http.post<MessageTemplateRenderResult>(`${this.base}/templates/preview`, payload);
  }

  listSuppressions(activeOnly = true, skip = 0, take = 50): Observable<PagedSuppressions> {
    const params = new HttpParams()
      .set('activeOnly', String(activeOnly))
      .set('skip', String(skip))
      .set('take', String(take));
    return this.http.get<PagedSuppressions>(`${this.base}/policy/suppressions`, { params });
  }

  createSuppression(payload: unknown): Observable<SuppressionEntry> {
    return this.http.post<SuppressionEntry>(`${this.base}/policy/suppressions`, payload);
  }

  revokeSuppression(suppressionId: string, reason: string): Observable<SuppressionEntry> {
    return this.http.post<SuppressionEntry>(`${this.base}/policy/suppressions/${suppressionId}/revoke`, { reason });
  }

  updateProviderRateLimit(configId: string, rateLimitPerMinute: number): Observable<{ providerConfigId: string; rateLimitPerMinute: number }> {
    return this.http.put<{ providerConfigId: string; rateLimitPerMinute: number }>(
      `${this.base}/providers/${configId}/rate-limit`,
      { rateLimitPerMinute });
  }

  listQuotaPolicies(): Observable<QuotaPolicy[]> {
    return this.http.get<QuotaPolicy[]>(`${this.base}/policy/quotas`);
  }

  upsertQuotaPolicy(payload: unknown): Observable<QuotaPolicy> {
    return this.http.put<QuotaPolicy>(`${this.base}/policy/quotas`, payload);
  }

  listQuotaUsage(): Observable<QuotaUsage[]> {
    return this.http.get<QuotaUsage[]>(`${this.base}/policy/quotas/usage`);
  }

  listDispatchPolicies(): Observable<NotificationDispatchPolicy[]> {
    return this.http.get<NotificationDispatchPolicy[]>(`${this.base}/policy/dispatch`);
  }

  upsertDispatchPolicy(payload: unknown): Observable<NotificationDispatchPolicy> {
    return this.http.put<NotificationDispatchPolicy>(`${this.base}/policy/dispatch`, payload);
  }

  listDeliveryMetrics(fromDate?: string | null, toDate?: string | null): Observable<DeliveryMetricBucket[]> {
    let params = new HttpParams();
    if (fromDate) params = params.set('fromDate', fromDate);
    if (toDate) params = params.set('toDate', toDate);
    return this.http.get<DeliveryMetricBucket[]>(`${this.base}/delivery/metrics`, { params });
  }

  getDeliveryDashboard(): Observable<DeliveryDashboard> {
    return this.http.get<DeliveryDashboard>(`${this.base}/delivery/dashboard`);
  }

  getProviderHealthDashboard(): Observable<ProviderHealthDashboard> {
    return this.http.get<ProviderHealthDashboard>(`${this.base}/providers/health-dashboard`);
  }

  listSenderReadiness(): Observable<SenderDomainReadiness[]> {
    return this.http.get<SenderDomainReadiness[]>(`${this.base}/delivery/sender-readiness`);
  }

  upsertSenderReadiness(payload: unknown): Observable<SenderDomainReadiness> {
    return this.http.put<SenderDomainReadiness>(`${this.base}/delivery/sender-readiness`, payload);
  }
}
